import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '@ui/buttons';
import { Menu } from '@ui/menus/Menu';
import { DropdownIndicator } from '@ui/DropdownIndicator';
import { Icon } from '@ui/Icon';
import { selectableQuantityUnits } from '@shared/models/unit.model';

type PerUnitOptionValue =
  (typeof selectableQuantityUnits)[number] | 'total' | 'GFA';

type SelectPerUnitOption = {
  value: PerUnitOptionValue;
  label: string;
};

type SelectPerUnitProps = {
  value: PerUnitOptionValue;
  options: SelectPerUnitOption[];
  onChange: (value: PerUnitOptionValue) => void;
};

const SelectPerUnit: React.FC<SelectPerUnitProps> = ({
  value,
  options,
  onChange,
}) => {
  const selectedLabel = useMemo(() => {
    return options.find((option) => option.value === value)?.label ?? value;
  }, [options, value]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const openMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) =>
      setAnchorEl(event.currentTarget),
    [],
  );
  const closeMenu = useCallback(() => setAnchorEl(undefined), []);
  const handleChange = useCallback(
    (nextValue: PerUnitOptionValue) => {
      onChange(nextValue);
      closeMenu();
    },
    [closeMenu, onChange],
  );

  return (
    <>
      <Button
        variant="text"
        color="neutral"
        size="small"
        textTransform="none"
        onClick={openMenu}
        endIcon={<DropdownIndicator open={Boolean(anchorEl)} />}
      >
        {selectedLabel}
      </Button>
      <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={closeMenu}>
        {options.map((option) => {
          const isSelected = option.value === value;
          return (
            <Menu.Item
              key={option.value}
              selected={isSelected}
              icon={
                isSelected ? <Icon category="mui" icon="Check" /> : undefined
              }
              onClick={() => handleChange(option.value)}
            >
              {option.label}
            </Menu.Item>
          );
        })}
      </Menu>
    </>
  );
};

export { SelectPerUnit, type SelectPerUnitOption, type PerUnitOptionValue };
